import * as React from 'react';
import styled from 'styled-components';
import { Text, Size } from '@openfin/ui-library';

import { BaseProps } from '../../types';
import { CNB } from '../../utils/ClassNameBuilder';
import { CenterPosition, ToastPosition } from '../../../store/partials/Settings';

import * as Styles from './PositionSelector.module.scss';

interface PositionSelectorProps extends BaseProps {
  id?: string;
  title: string;
  onChange?: (position: CenterPosition) => void;
  value?: CenterPosition;
}

export const PositionSelector: React.FC<PositionSelectorProps> = (props) => {
  const { value, title, id = '', onChange = () => {} } = props;
  const handleKeyPress = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        event.stopPropagation();
        if (value === 'left') {
          onChange('right');
        }
        if (value === 'right') {
          onChange('left');
        }
        break;
    }
  };

  return (
    <div role="radiogroup" className={Styles.positionSelector}>
      <TitleElement htmlFor={id} size={Size.large}>
        {title}
      </TitleElement>
      <div tabIndex={0} onKeyUp={handleKeyPress} id={id} className={Styles.selectors}>
        <Selector
          className="left-selector"
          position="left"
          onClick={onChange}
          active={value === 'left'}
        />
        <Selector
          className="right-selector"
          position="right"
          onClick={onChange}
          active={value === 'right'}
        />
      </div>
    </div>
  );
};

interface QuadPositionSelectorProps extends BaseProps {
  id?: string;
  title: string;
  onChange?: (position: ToastPosition) => void;
  value?: ToastPosition;
}

export const QuadSelector: React.FC<QuadPositionSelectorProps> = (props) => {
  const { value, title, id = '', onChange = () => {} } = props;
  const positions: ToastPosition[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
  const handleKeyPress = (event: React.KeyboardEvent) => {
    const currentIndex = positions.findIndex((pos) => pos === value);
    const totalItems = positions.length;
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        event.stopPropagation();
        if (currentIndex === 0) {
          onChange('bottom-right');
        } else {
          onChange(positions[currentIndex - 1]);
        }
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        event.stopPropagation();
        if (currentIndex === totalItems - 1) {
          onChange('top-left');
        } else {
          onChange(positions[currentIndex + 1]);
        }
        break;
    }
  };
  return (
    <div role="radiogroup" className={Styles.positionSelector}>
      <TitleElement htmlFor={id} size={Size.large}>
        {title}
      </TitleElement>
      <div
        tabIndex={0}
        onKeyUp={handleKeyPress}
        id={id}
        className={CNB.join(Styles, Styles.quad, Styles.selectors)}
      >
        {positions.map((pos) => (
          <Selector
            key={pos}
            className={`${pos}-selector`}
            position={pos}
            onClick={onChange}
            active={value === pos}
          />
        ))}
      </div>
    </div>
  );
};

interface SelectorProps<T = CenterPosition | ToastPosition> extends BaseProps {
  position: T;
  onClick?: (position: T) => void;
  active?: boolean;
  id?: string;
}

function Selector<T = CenterPosition | ToastPosition>(props: SelectorProps<T>): React.ReactElement {
  const { position, className, active = false, onClick = () => {}, ...rest } = props;

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onClick(position);
    }
  };

  return (
    <div
      role="radio"
      className={CNB.join(Styles, Styles.container, className)}
      onClick={() => onClick(position)}
      onKeyPress={handleKeyPress}
      aria-checked={active}
      aria-label={(position as unknown) as string}
      {...rest}
    >
      <div className={Styles.selector}>
        <div className={CNB.join(Styles, Styles.bar, (position as unknown) as string)}></div>
      </div>
      <div className={Styles.text}>{position}</div>
    </div>
  );
}

const TitleElement = styled(Text).attrs({ as: 'label' })<{ htmlFor: string }>`
  margin-bottom: ${({ theme }) => theme.px.base};
`;
